<script>
import { ElMessage } from "element-plus";
import SearchIcon from "@/icons/SearchIcon.vue";
import { getDictionaryResult } from "@/api/dictionary";
import CopyIcon from "@/icons/CopyIcon.vue";
import { languages } from "@/api/common";

export default {
  name: "Dictionary",
  components: { CopyIcon, SearchIcon },
  data() {
    return {
      query: "",
      results: [],
      loading: false,
      error: null,
      searched: false,

      //filters
      selectedLanguages: ["kz", "ru"],
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    languages() {
      return languages().filter((l) => l.value !== "mx");
    },
  },
  mounted() {
    this.$refs.searchInput?.focus();
  },
  methods: {
    async handleSearch() {
      const text = this.query.trim();

      if (text.length < 3) {
        ElMessage({
          message: "Введите минимум 3 символа",
          type: "warning",
          placement: "top-right",
        });
        return;
      }
      const hasLetter = /[A-Za-zА-Яа-яӘәҒғҚқҢңӨөҰұҮүҺһІі]/.test(text);
      if (!hasLetter) {
        ElMessage({
          message: "Введите хотя бы одну букву",
          type: "warning",
          placement: "top-right",
        });
        return;
      }
      if (this.selectedLanguages.length === 0) {
        this.searched = true;
        this.results = [];
        this.error = null;
        return;
      }

      this.loading = true;
      this.error = null;
      this.results = [];
      this.searched = true;

      try {
        const payload = {
          text: this.query,
        };

        if (this.selectedLanguages.length === 1) {
          payload.lang = this.selectedLanguages[0];
        }

        const response = await getDictionaryResult(payload);
        this.results = response?.success_words || [];
      } catch (err) {
        this.error = "Ошибка при поиске. Попробуйте снова.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async toggleLanguage(value) {
      if (this.selectedLanguages.includes(value)) {
        this.selectedLanguages = this.selectedLanguages.filter(
          (l) => l !== value
        );
      } else {
        this.selectedLanguages.push(value);
      }
      if (this.searched) await this.handleSearch();
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        ElMessage({
          message: `Скопировано: ${text}`,
          type: "success",
          placement: "top-right",
        });
      } catch {
        ElMessage({
          message: "Не удалось скопировать текст.",
          type: "error",
          placement: "top-right",
        });
      }
    },
  },
};
</script>

<template>
  <div class="search-wrapper">
    <div class="search-box-wrapper">
      <div class="search-box">
        <SearchIcon class="search-icon" />
        <input
          v-model="query"
          @keyup.enter.prevent="handleSearch"
          ref="searchInput"
          type="text"
          placeholder="Поиск слова..."
          class="search-input"
        />
        <button @click="handleSearch" class="search-btn" :disabled="loading">
          {{ loading ? "Поиск..." : "Найти" }}
        </button>
      </div>
      <div
        class="filters w-full flex justify-between mt-4 gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50"
      >
        <span class="text-gray-600 font-medium">Фильтры:</span>
        <div class="language-filters flex gap-3.5">
          <div
            v-for="lang in languages"
            :key="lang.value"
            class="flex items-center gap-2"
          >
            <input
              :checked="selectedLanguages.includes(lang.value)"
              @change="toggleLanguage(lang.value)"
              type="checkbox"
              class="cursor-pointer"
              style="height: 18px; width: 18px"
            />
            <span>{{ lang.title }}</span>
          </div>
        </div>
      </div>
      <div
        v-if="searched"
        class="results-container mt-4 w-full overflow-y-auto border border-solid border-gray-200 bg-white rounded-lg"
      >
        <div class="search-results" v-if="!loading && results.length">
          <div
            v-for="result in results"
            :key="result._id"
            class="flex justify-between w-full px-4 py-2 border-0 border-b border-solid border-gray-200"
          >
            <div>
              <strong class="text-gray-500 mr-10">{{ result.key }}:</strong>
              <span style="white-space: pre-wrap; tab-size: 4">{{
                result.text
              }}</span>
            </div>
            <div class="flex items-center">
              <CopyIcon
                @click="copyText(result.clean_text)"
                class="cursor-pointer text-gray-600 hover:text-blue-500"
                style="height: 16px; width: 16px"
              />
            </div>
          </div>
        </div>

        <div
          v-if="!loading && !results.length && !error"
          class="no-results mt-10"
        >
          Ничего не найдено.
        </div>

        <div v-if="error" class="error-msg">{{ error }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.search-wrapper {
  @apply relative flex flex-col items-center px-6 pt-20 pb-28;
  height: 100vh;
  box-sizing: border-box;
  overflow: hidden;

  .search-results {
    @apply flex flex-col w-full;
  }
}

.search-box-wrapper {
  @apply flex flex-col w-full;
  max-width: 600px;
  min-height: 0;
  flex: 1;
}

.results-container {
  flex: 1;
  min-height: 0;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  width: 100%;
  padding: 0.5rem 0.85rem;
  flex-shrink: 0;

  &:focus-within {
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
  }
}

.filters {
  flex-shrink: 0;
}

.search-icon {
  @apply text-gray-400;
  width: 22px;
  height: 22px;
  margin-right: 10px;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  color: #1f2937;
  background: transparent;

  &::placeholder {
    color: #9ca3af;
  }
}

.search-btn {
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.4rem 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #1e40af;
  }
}
</style>
